import {
  AuditQuestionCompletionStatus,
  ResponseResult,
  type Audit,
  type AuditMetrics,
  type GetAuditSummaryCommandOutput,
  type QuestionResponseDetails,
  type SubQuestionResponseEntry,
} from '@amzn/gbsa-helios-typescript-client';
import { AuditQueryKeys } from '../api/keys';

/**
 * Shared plumbing for the optimistic audit-summary cache patches.
 * Answer patches live in patchAuditAnswerCaches; field patches
 * (comment / naReason / mediaIds) in patchAuditFieldCaches.
 */

/** Query predicate matching cached audit-summary entries for one audit. */
export function auditSummaryPredicate(auditId: string) {
  return (query: { queryKey: readonly unknown[] }): boolean => {
    if (!Array.isArray(query.queryKey) || query.queryKey[0] !== AuditQueryKeys.summary) {
      return false;
    }
    const input = query.queryKey[1] as { auditId?: string } | undefined;
    return input?.auditId === auditId;
  };
}

/**
 * Base for patches on a slot that had no response row yet. Cast: the SDK
 * requires responseResult but runtime payloads allow partial rows.
 */
export const EMPTY_RESPONSE = {
  responseStatus: AuditQuestionCompletionStatus.NOT_STARTED,
  responseScore: 0,
  respondedBy: '',
  respondedAt: new Date(0),
} as unknown as QuestionResponseDetails;

/** Immutably assemble the next cache shape for one patched question. */
export function buildNextCacheData(
  cachedData: GetAuditSummaryCommandOutput,
  targetIndex: number,
  nextMainResponse: QuestionResponseDetails,
  nextSubResponses: SubQuestionResponseEntry[],
  nextMetrics: AuditMetrics | undefined,
): GetAuditSummaryCommandOutput {
  const nextQuestions = cachedData.questions.map((q, i) =>
    i === targetIndex
      ? {
          ...q,
          responseDetails: {
            ...q.responseDetails,
            mainQuestionResponse: nextMainResponse,
            subQuestionResponses: nextSubResponses,
          },
        }
      : q,
  );
  const nextAudit: Audit = nextMetrics
    ? { ...cachedData.audit, metrics: nextMetrics }
    : cachedData.audit;
  return { ...cachedData, audit: nextAudit, questions: nextQuestions };
}

/**
 * Diff-update AuditMetrics for one response transitioning from
 * `previousResult` to `nextResult`. Two independent signals:
 * completion moves `completedQuestions` (inferred from result presence
 * unless the sub-question path passes explicit overrides — a mixed
 * parent is done without an aggregate), and the result transition
 * moves the pass/fail/na buckets. Returns undefined on a no-op.
 */
export function applyMetricsDiff(
  metrics: AuditMetrics | undefined,
  previousResult: ResponseResult | undefined,
  nextResult: ResponseResult | undefined,
  previousCompleted?: boolean,
  nextCompleted?: boolean,
): AuditMetrics | undefined {
  if (!metrics) return undefined;

  const wasCompleted = previousCompleted ?? previousResult !== undefined;
  const isCompleted = nextCompleted ?? nextResult !== undefined;

  const completionChanged = wasCompleted !== isCompleted;
  const resultChanged = previousResult !== nextResult;
  if (!completionChanged && !resultChanged) return undefined;

  const nextCounters = readCountersWithNaNGuard(metrics);
  applyCompletionDelta(nextCounters, wasCompleted, isCompleted);
  if (resultChanged) {
    applyResultBucketDelta(nextCounters, previousResult, nextResult);
  }

  return { ...metrics, ...nextCounters };
}

interface ResultCounters {
  passedQuestions: number;
  failedQuestions: number;
  naQuestions: number;
  completedQuestions: number;
}

// `?? 0` per counter: fresh NOT_STARTED audits omit them, and
// `undefined + 1` would poison the cache with NaN.
function readCountersWithNaNGuard(metrics: AuditMetrics): ResultCounters {
  return {
    completedQuestions: metrics.completedQuestions ?? 0,
    passedQuestions: metrics.passedQuestions ?? 0,
    failedQuestions: metrics.failedQuestions ?? 0,
    naQuestions: metrics.naQuestions ?? 0,
  };
}

function applyCompletionDelta(
  counters: ResultCounters,
  wasCompleted: boolean,
  isCompleted: boolean,
): void {
  if (!wasCompleted && isCompleted) counters.completedQuestions += 1;
  else if (wasCompleted && !isCompleted) counters.completedQuestions -= 1;
}

function applyResultBucketDelta(
  counters: ResultCounters,
  previousResult: ResponseResult | undefined,
  nextResult: ResponseResult | undefined,
): void {
  if (previousResult !== undefined) decrementResultCounter(counters, previousResult);
  if (nextResult !== undefined) incrementResultCounter(counters, nextResult);
}

function incrementResultCounter(counters: ResultCounters, result: ResponseResult): void {
  if (result === ResponseResult.PASS) counters.passedQuestions += 1;
  else if (result === ResponseResult.FAIL) counters.failedQuestions += 1;
  else if (result === ResponseResult.NA) counters.naQuestions += 1;
}

function decrementResultCounter(counters: ResultCounters, result: ResponseResult): void {
  if (result === ResponseResult.PASS) counters.passedQuestions -= 1;
  else if (result === ResponseResult.FAIL) counters.failedQuestions -= 1;
  else if (result === ResponseResult.NA) counters.naQuestions -= 1;
}
